/**
 * Checkers-specific gameplay messages, layered on top of the shared room
 * envelope from `@bn-games/network-protocol`. Mirror of
 * `packages/game-sessions/src/checkers/protocol.ts` — keep both in sync.
 */

import type {
  LobbyClientMessage,
  LobbyServerMessage,
  StateSyncMessage as EnvelopeStateSyncMessage,
} from '@bn-games/network-protocol';
import type { Color, Move, Square } from '../game/types.js';
import type { Variant } from '../settings/types.js';

const KNOWN_VARIANTS: readonly Variant[] = ['russian', 'english'];

/**
 * Narrow the optional `variant` field on shared lobby messages to a known
 * checkers Variant. Defaults to `'russian'` when the server didn't supply a
 * value (older clients / rooms created before the field shipped).
 */
export function parseVariant(field: string | undefined): Variant {
  if (typeof field === 'string' && (KNOWN_VARIANTS as readonly string[]).includes(field)) {
    return field as Variant;
  }
  return 'russian';
}

/**
 * The shared protocol carries a numeric `seat`; checkers maps seat→color via
 * this constant. Mirror of `CHECKERS_SEATS` in
 * `packages/game-sessions/src/checkers/CheckersSession.ts` — both must agree.
 */
export type Player = Color;
export const SEATS: readonly Player[] = ['white', 'black'];

export type {
  ErrorCode,
  ReconnectFailReason,
  SeatedPlayer,
  CreateRoomMessage,
  RoomCreatedMessage,
  JoinRoomMessage,
  RoomJoinedMessage,
  OpponentJoinedMessage,
  GameStartMessage,
  RematchRequestMessage,
  RematchAcceptMessage,
  RematchDeclineMessage,
  RematchStartMessage,
  OpponentDisconnectedMessage,
  OpponentReconnectedMessage,
  ReconnectRoomMessage,
  ReconnectOkMessage,
  ReconnectFailMessage,
  RequestStateMessage,
  ErrorMessage,
  PingMessage,
  PongMessage,
} from '@bn-games/network-protocol';

// --- Gameplay (checkers) ---

export interface CheckersMovePayload {
  readonly from: Square;
  readonly to: Square;
  readonly path: readonly Square[];
  readonly captures: readonly Square[];
  readonly promoted: boolean;
}

export interface CheckersMoveMessage {
  readonly type: 'CHECKERS_MOVE';
  readonly move: CheckersMovePayload;
}

export interface CheckersGameOverMessage {
  readonly type: 'CHECKERS_GAME_OVER';
  readonly result: 'win' | 'draw';
  readonly winner?: Player;
}

// --- Snapshot for mid-game reconnect ---

export interface SerializedPiece {
  readonly row: number;
  readonly col: number;
  readonly color: Color;
  readonly kind: 'man' | 'king';
}

export interface GameSnapshot {
  readonly pieces: readonly SerializedPiece[];
  readonly currentPlayer: Color;
  readonly moveCount: number;
  readonly quietMoveCount: number;
}

export type StateSyncMessage = EnvelopeStateSyncMessage<GameSnapshot>;

// --- Unions ---

export type ClientMessage =
  | LobbyClientMessage
  | CheckersMoveMessage
  | CheckersGameOverMessage
  | StateSyncMessage;

export type ServerMessage =
  | LobbyServerMessage
  | CheckersMoveMessage
  | CheckersGameOverMessage
  | StateSyncMessage;

// --- Helpers: Move <-> CheckersMovePayload ---

export function serializeMove(move: Move): CheckersMovePayload {
  return {
    from: { row: move.from.row, col: move.from.col },
    to: { row: move.to.row, col: move.to.col },
    path: move.path.map((sq) => ({ row: sq.row, col: sq.col })),
    captures: move.captures.map((sq) => ({ row: sq.row, col: sq.col })),
    promoted: move.promoted,
  };
}

export function deserializeMove(payload: CheckersMovePayload): Move {
  return {
    from: { row: payload.from.row, col: payload.from.col },
    to: { row: payload.to.row, col: payload.to.col },
    path: payload.path.map((sq) => ({ row: sq.row, col: sq.col })),
    captures: payload.captures.map((sq) => ({ row: sq.row, col: sq.col })),
    promoted: payload.promoted,
  };
}

/**
 * Resolve the checkers color for a given seat number (with bounds-checking).
 * Throws on a bad seat — caller is expected to pass the value from a valid
 * lobby envelope.
 */
export function colorForSeat(seat: number): Player {
  const color = SEATS[seat];
  if (color === undefined) {
    throw new Error(`colorForSeat: seat ${seat} out of range`);
  }
  return color;
}

/**
 * Resolve the local player's color from the server's authoritative seat→role
 * mapping when available, falling back to the static `SEATS` constant. The
 * wire mapping is post-rotation, so this is correct after `preferredSeat` at
 * game start and after a rematch — the static array, by contrast, never moves.
 */
export function resolveSeatColor(
  seats: readonly string[] | null | undefined,
  seat: number,
): Player {
  const raw = seats?.[seat];
  if (raw === 'white' || raw === 'black') return raw;
  return colorForSeat(seat);
}
